import { motion } from "framer-motion";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useTestMutation from "../../api/mutations/testMutation";
import circle from "../../assets/enter-code-circle.svg";
import { CustomProgressBar } from "../../components/CustomProgressBar";
import { CustomSpinner } from "../../components/CustomSpinner";
import ErrorPage from "./ErrorTestPage";

const WELCOME_TEXT = (userName: string) =>
  `Välkommen till testet ${userName ? `, ${userName}` : ""}`;
const TEST_DESCRIPTION = (total: number | string) =>
  `Testet har ${total} frågor och du besvarar dessa på ca 5 minuter. När du besvarat en fråga tas du automatiskt till nästa. Det går inte att backa tillbaka.`;
const QUESTION_LABEL = "Fråga: ";
const SESSION_EXPIRED_MESSAGE = "Möjligheten att utföra testet har avslutats";
const NO_SESSION_ID_ERROR = "Det gick inte att ladda testet";

const TestPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [question, setQuestion] = useState<string | null>(null);
  const [questionId, setQuestionId] = useState<string | null>(null);
  const [flip, setFlip] = useState<boolean>(false);

  const [isLoadingQuestion, setIsLoadingQuestion] = useState<boolean>(false);
  const [isAnswering, setIsAnswering] = useState<boolean>(false);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(false);
  const [possibleAnswers, setPossibleAnswers] = useState<any[]>([]);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(1);
  const [totalQuestions, setTotalQuestions] = useState<number>(0);
  const navigate = useNavigate();
  const { nextQuestion, answerQuestion } = useTestMutation();

  const isn = localStorage.getItem("isn");
  const sessionId = localStorage.getItem("sessionId");
  const storedUserName = localStorage.getItem("userName");

  const [userName, setUserName] = useState<string>("");

  // Retrieve the user name from localStorage
  useEffect(() => {
    setUserName(storedUserName || "");
  }, []);

  // Immediately show error if session ID or ISN is missing
  useEffect(() => {
    if (!sessionId || !isn) {
      setError(NO_SESSION_ID_ERROR);
    }
  }, [sessionId, isn]);

  const fetchNextQuestion = async (isn: string, sessionId: string) => {
    setIsLoadingQuestion(true);
    let attempts = 0;
    const maxAttempts = 5; // Maximum number of attempts
    let success = false; // Flag to track success

    while (attempts < maxAttempts && !success) {
      try {
        const response = await nextQuestion.trigger({ isn, sessionId });
        if (response.data.data) {
          setQuestion(response.data.data.question);
          setPossibleAnswers(response.data.data.possibleAnswers);
          setQuestionId(response.data.data.testFlowQuestionId);
          setFlip(response.data.data.flip || false);
          setCurrentQuestionIndex(
            response.data.data?.answeredQuestionsCount + 1
          ); // Increment question index
          const total =
            response.data.data?.answeredQuestionsCount +
            response.data.data?.notAnsweredQuestionsCount;
          setTotalQuestions(total);
          success = true; // Mark success if the request was successful
        } else if (!_.isEmpty(response.data.error)) {
          const { message, code } = response.data.error || {};
          success = true;
          setError(message || "Ett okänt fel inträffade.");
        }
      } catch (error: any) {
        attempts += 1; // Increment attempts on error
        const errorMessage =
          error?.response?.data?.error?.message ||
          error.message ||
          "Ett okänt fel inträffade efter flera försök.";
        if (attempts === maxAttempts) {
          setError(errorMessage); // Display error after max attempts
        }
      } finally {
        setIsLoadingQuestion(false);
        setIsInitialLoading(false);
      }
    }
  };

  const handleAnswer = async (index: number) => {
    if (sessionId && isn) {
      setIsAnswering(true);
      try {
        const score = flip ? possibleAnswers.length - index : index + 1;

        // Send the score for the answer
        await answerQuestion.trigger({
          isn,
          sessionId,
          questionId: String(questionId || ""),
          score,
        });

        if (totalQuestions !== currentQuestionIndex) {
          // Fetch the next question
          await fetchNextQuestion(isn, sessionId);
        }

        if (totalQuestions === currentQuestionIndex) {
          // If there are no unanswered questions, complete the test
          await handleCompleteTest();
        }
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error.message ||
          "Kunde inte svara på denna fråga. Försök igen.";
        toast.error(errorMessage, {
          style: {
            backgroundColor: "#3F51B5",
          },
        });
      } finally {
        setIsAnswering(false);
      }
    }
  };

  const handleCompleteTest = async () => {
    if (sessionId && isn) {
      try {
        navigate("/result");
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error.message ||
          "Ett okänt fel inträffade.";
        toast.error(errorMessage, {
          style: {
            backgroundColor: "#3F51B5",
          },
        });
      }
    }
  };

  useEffect(() => {
    // Send GET request on component mount
    if (sessionId && isn && storedUserName) {
      setIsInitialLoading(true);
      fetchNextQuestion(isn, sessionId);
    }

    // Handle cleanup on page unload
    // const handleUnload = () => {
    //   localStorage.removeItem("sessionId");
    //   localStorage.removeItem("isn");
    //   localStorage.removeItem("userName");
    // };

    // window.addEventListener("beforeunload", handleUnload);

    return () => {
      // window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  useEffect(() => {
    if (!sessionId || !isn || !storedUserName) {
      setError(SESSION_EXPIRED_MESSAGE);
    }
  }, []);

  if (isInitialLoading) {
    return (
      <div className="flex flex-col justify-center items-center w-full lg:min-h-[calc(100vh-160px)]">
        <CustomSpinner />
      </div>
    );
  }

  if (error) {
    return <ErrorPage message={error} />;
  }

  return (
    <main className="items-start pt-5 sm:pt-0 sm:flex-grow sm:h-full flex sm:items-center justify-center">
      <div className="flex flex-col justify-center items-center w-full">
        <div className="max-w-[1000px] flex flex-col gap-[60px] w-full h-full justify-center items-center md:px-10">
          <div className="gap-8 w-full h-full bg-primary-card rounded-lg p-9 min-h-[500px]">
            <div className="flex flex-col gap-8 items-center justify-center text-start">
              <div className="flex flex-col gap-8 items-center justify-center">
                <h1 className="text-white text-2xl md:text-[32px] font-normal break-words font-nunito leading-normal">
                  {WELCOME_TEXT(userName)}
                </h1>
                <h5 className="text-white text-base lg:text-xl font-nunito leading-snug">
                  {TEST_DESCRIPTION(totalQuestions)}
                </h5>
                <div className="w-full h-[2px] bg-[#3F51B5]"></div>
                <div className="w-full flex flex-col justify-start items-start gap-3">
                  <h5 className="text-white text-xl font-nunito leading-snug">
                    {`${QUESTION_LABEL} ${currentQuestionIndex}/${totalQuestions}`}
                  </h5>
                  <CustomProgressBar
                    currentQuestion={currentQuestionIndex}
                    totalQuestions={totalQuestions}
                  />
                </div>
                <div className="min-h-[70px] sm:min-h-[60px]">
                  {isLoadingQuestion ? (
                    <h5 className="text-white text-xl font-nunito leading-snug">
                      Laddar...
                    </h5>
                  ) : (
                    <h5
                      className="text-white test-base lg:text-xl font-nunito leading-snug"
                      dangerouslySetInnerHTML={{ __html: question! }}
                    ></h5>
                  )}
                </div>
              </div>
              <div className="flex items-end justify-center gap-5 w-full">
                <div className="hidden md:flex flex-col items-center">
                  <p className="text-white text-sm md:text-lg">Stämmer</p>
                  <p className="text-white text-sm md:text-lg">inte alls</p>
                </div>
                <div className="flex flex-wrap justify-center gap-3">
                  {possibleAnswers.map((_, index) => (
                    <label
                      key={index}
                      className="flex flex-col items-center text-white"
                    >
                      <span className="text-white mb-5">{index + 1}</span>
                      <input
                        type="radio"
                        name="answer"
                        value={index + 1}
                        onClick={() => handleAnswer(index)}
                        disabled={isAnswering}
                        className="hidden"
                      />
                      {/* <motion.div
                      whileTap={{
                        scale: 1.2,
                        transition: { duration: 0.1 },
                      }}
                      className="h-[26px] w-[26px] flex justify-center items-center rounded-full border-2 border-[#3F51B5] hover:bg-[#3F51B5] focus:scale-125 transition duration-150 ease-in-out"
                    >
                      &nbsp;
                    </motion.div> */}
                      <div className="testBtn lg:hover:bg-[#3F51B5]"></div>
                    </label>
                  ))}
                </div>
                <div className="hidden md:flex flex-col items-center">
                  <p className="text-white text-sm md:text-lg">Stämmer</p>
                  <p className="text-white text-sm md:text-lg">
                    helt och hållet
                  </p>
                </div>
              </div>
              <div className="flex md:hidden flex-row justify-between items-center w-full">
                <p className="text-white text-sm md:text-lg">
                  Stämmer inte alls
                </p>
                <p className="text-white text-sm md:text-lg">
                  Stämmer helt och hållet
                </p>
              </div>
            </div>
          </div>
          <img
            src={circle}
            alt="main-photo"
            className="absolute bottom-16 right-0  w-[230px] h-[230px] md:w-[400px] md:h-[400px] -z-10"
          />
        </div>
      </div>
    </main>
  );
};

export default TestPage;
